import type {
  CrossRefColumnConfig,
  CrossReferenceRowState,
  CrossReferenceState,
  CrossReferenceWireEntry,
  CrossReferenceWireMap,
  CrossReferenceWireRow,
} from '../types';

// Wire -> in-memory. Decimal strings become numbers; placeholder is false.
// Non-finite parses (e.g. legacy garbage data) collapse to null so they
// don't surface as `NaN` in match comparisons or formatNumbers output.
export const wireRowToState = (row: CrossReferenceWireRow): CrossReferenceRowState => {
  const parsed = row.value === null || row.value === undefined ? null : Number(row.value);
  return {
    position: row.position,
    value: parsed !== null && Number.isFinite(parsed) ? parsed : null,
    metadata: row.metadata ?? {},
    isPlaceholder: false,
  };
};

export const wireEntryToState = (entry: CrossReferenceWireEntry): CrossReferenceState => {
  const rows: CrossReferenceRowState[] = entry.rows.map(wireRowToState);
  if (entry.row_mode === 'multi_row') {
    // Ensure a trailing placeholder always exists for multi_row.
    const nextPosition = rows.length ? Math.max(...rows.map(r => r.position)) + 1 : 0;
    rows.push({ position: nextPosition, value: null, metadata: {}, isPlaceholder: true });
  } else if (rows.length === 0) {
    rows.push({ position: 0, value: null, metadata: {}, isPlaceholder: false });
  }
  return {
    rowMode: entry.row_mode,
    columnsConfig: (entry.columns_config as CrossRefColumnConfig[]) ?? [],
    rows,
    isDirty: false,
  };
};

// In-memory -> wire. Placeholder rows are dropped; positions stay as-authored.
export const stateToWireEntry = (state: CrossReferenceState): CrossReferenceWireEntry => ({
  row_mode: state.rowMode,
  columns_config: state.columnsConfig,
  rows: state.rows
    .filter(r => !r.isPlaceholder)
    .map(r => ({
      position: r.position,
      value: r.value === null || r.value === undefined ? null : String(r.value),
      metadata: r.metadata ?? {},
    })),
});

export const wireMapToStateMap = (wireMap: CrossReferenceWireMap): Record<string, CrossReferenceState> => {
  const out: Record<string, CrossReferenceState> = {};
  Object.entries(wireMap ?? {}).forEach(([alias, entry]) => {
    out[alias] = wireEntryToState(entry);
  });
  return out;
};
